'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import { useLang } from '@/lib/langContext';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface RegisterFormData {
  fullName: string;
  email: string;
  mobile: string;
  password: string;
  confirmPassword: string;
}

interface Props {
  onSwitchToLogin: () => void;
  onRegistered: (email: string) => void;
}

export default function RegisterForm({ onSwitchToLogin, onRegistered }: Props) {
  const { t, dir, lang } = useLang();
  const { signUp } = useAuth();
  const router = useRouter();
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const isAr = lang === 'ar';

  const { register, handleSubmit, watch, formState: { errors } } = useForm<RegisterFormData>();
  const passwordValue = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setAuthError(null);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        mobile: data.mobile,
      });
      router.push('/dashboard');
    } catch (error: any) {
      const msg = error?.message || '';
      if (msg.includes('User already registered') || msg.includes('already been registered')) {
        setAuthError(
          isAr
            ? 'هذا البريد الإلكتروني مسجل مسبقاً، يرجى تسجيل الدخول' :'This email is already registered. Please sign in.'
        );
      } else if (msg.includes('Password should be')) {
        setAuthError(
          isAr ? 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' : 'Password must be at least 8 characters'
        );
      } else {
        setAuthError(msg || (isAr ? 'حدث خطأ، حاول مرة أخرى' : 'An error occurred, please try again'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fade-in">
      <div className="mb-6">
        <h2 className="text-3xl font-800 text-foreground mb-2">{t.register}</h2>
        <p className="text-muted-foreground text-base">
          {isAr ? 'أنشئ حسابك وابدأ تتبع وثائقك' : 'Create your account and start tracking documents'}
        </p>
      </div>

      {authError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2 text-sm text-red-700">
          <Icon name="ExclamationCircleIcon" size={16} className="flex-shrink-0" />
          {authError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {/* Full Name */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">
            {t.fullName} <span className="text-status-expired">*</span>
          </label>
          <input
            type="text"
            className={`input-base w-full px-4 py-2.5 ${errors.fullName ? 'border-status-expired' : ''}`}
            placeholder={isAr ? 'أحمد محمد الراشد' : 'Ahmed Al-Rashid'}
            {...register('fullName', { required: t.required })}
          />
          {errors.fullName && <p className="mt-1 text-xs text-status-expired">{errors.fullName.message}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">
            {t.email} <span className="text-status-expired">*</span>
          </label>
          <input
            type="email"
            className={`input-base w-full px-4 py-2.5 ${errors.email ? 'border-status-expired' : ''}`}
            placeholder="you@example.com"
            {...register('email', {
              required: t.required,
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t.invalidEmail },
            })}
          />
          {errors.email && <p className="mt-1 text-xs text-status-expired">{errors.email.message}</p>}
        </div>

        {/* Mobile (optional) */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">
            {t.mobile}
            <span className="text-muted-foreground font-400 ms-1">({isAr ? 'اختياري' : 'optional'})</span>
          </label>
          <input
            type="tel"
            className="input-base w-full px-4 py-2.5"
            placeholder="+966 5x xxx xxxx"
            {...register('mobile')}
          />
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">
            {t.password} <span className="text-status-expired">*</span>
          </label>
          <div className="relative">
            <input
              type={showPass ? 'text' : 'password'}
              className={`input-base w-full px-4 py-2.5 pe-12 ${errors.password ? 'border-status-expired' : ''}`}
              placeholder="••••••••"
              {...register('password', {
                required: t.required,
                minLength: { value: 8, message: t.passwordLength },
              })}
            />
            <button type="button" onClick={() => setShowPass(p => !p)} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <Icon name={showPass ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-status-expired">{errors.password.message}</p>}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">
            {t.confirmPassword} <span className="text-status-expired">*</span>
          </label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              className={`input-base w-full px-4 py-2.5 pe-12 ${errors.confirmPassword ? 'border-status-expired' : ''}`}
              placeholder="••••••••"
              {...register('confirmPassword', {
                required: t.required,
                validate: v => v === passwordValue || t.passwordMismatch,
              })}
            />
            <button type="button" onClick={() => setShowConfirm(p => !p)} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-1 text-xs text-status-expired">{errors.confirmPassword.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 rounded-lg text-base font-600 flex items-center justify-center gap-2 disabled:opacity-70 mt-2"
        >
          {isLoading ? (
            <><Icon name="ArrowPathIcon" size={18} className="animate-spin" />{t.loading}</>
          ) : t.register}
        </button>
      </form>

      <p className="mt-5 text-center text-sm text-muted-foreground">
        {t.hasAccount}{' '}
        <button onClick={onSwitchToLogin} className="text-primary font-600 hover:underline">{t.signInNow}</button>
      </p>
    </div>
  );
}